import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { VisitorDetailDialog } from "@project/admin/components/visitor-detail-dialog";
import { VisitorFormFields } from "@project/admin/components/visitor-form-fields";
import type { Field } from "@project/admin/components/resource-table";
import { projectToast } from "@project/lib/toast";
import { visitorService } from "@project/services/admin/visitor.service";
import {
  buildVisitorPayload,
  mapVisitorToForm,
  visitorDefaultValues,
  type VisitorRecord,
} from "@project/types/visitor";

const tableFields: Field[] = [
  {
    name: "name",
    label: "Visitor Name",
    showInTable: true,
    hidden: true,
  },
  {
    name: "mobile_number",
    label: "Mobile",
    showInTable: true,
    hidden: true,
  },
  {
    name: "date",
    label: "Visit Date",
    type: "date",
    showInTable: true,
    hidden: true,
  },
  {
    name: "reason_for_visit",
    label: "Reason",
    showInTable: true,
    hidden: true,
  },
  {
    name: "occupation",
    label: "Occupation",
    showInTable: true,
    hidden: true,
  },
  {
    name: "email",
    label: "Email",
    showInTable: true,
    hidden: true,
  },
];

export default function Page() {
  const [viewTarget, setViewTarget] = useState<VisitorRecord | null>(null);

  return (
    <AdminShell title="Visitors Feedback" subtitle="Visitor log book">
      <ApiResourceTable<VisitorRecord>
        title="Visitor"
        labelField="name"
        fields={tableFields}
        searchFields={[
          "name",
          "mobile_number",
          "email",
          "reason_for_visit",
          "occupation",
        ]}
        defaultValues={visitorDefaultValues()}
        enableCsvExport
        csvFileName="visitors"
        dialogClassName="max-w-3xl"
        addButtonLabel="Add Visitor"
        list={query => visitorService.listVisitorsPage(query)}
        loadRecordForEdit={id => visitorService.getVisitor(id)}
        create={payload =>
          visitorService.createVisitor(buildVisitorPayload(payload))
        }
        update={(id, payload) =>
          visitorService.updateVisitor(id, buildVisitorPayload(payload))
        }
        remove={id => visitorService.deleteVisitor(id)}
        onView={row => setViewTarget(row)}
        renderForm={({ editing, setEditing }) => (
          <VisitorFormFields editing={editing} setEditing={setEditing} />
        )}
        buildPayload={editing => {
          if (!editing) {
            return null;
          }
          try {
            return buildVisitorPayload(editing as Record<string, unknown>);
          } catch (error) {
            projectToast.error(
              error instanceof Error ? error.message : "Invalid visitor form"
            );
            return null;
          }
        }}
        mapRecordToForm={record =>
          mapVisitorToForm(record) as unknown as Partial<VisitorRecord>
        }
      />

      <VisitorDetailDialog
        open={!!viewTarget}
        onOpenChange={open => {
          if (!open) {
            setViewTarget(null);
          }
        }}
        visitorId={viewTarget?.id ?? null}
        preview={viewTarget}
      />
    </AdminShell>
  );
}
